import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import HrnbButton from '../controls/HrnbButton';
import HrnbAlert from '../controls/HrnbAlert';
import { acceptDuelInvite, declineDuelInvite, getMyDuelInvites } from '../../api/duels';
import '../../css/chats/DuelInviteAttachment.css';

/**
 * Вложение-приглашение в дуэль
 * @param {Object} attachment - Данные вложения
 * @param {string} attachment.entityId - ID дуэли
 * @param {string} attachment.title - Название дуэли
 * @param {string} attachment.inviteType - Тип: PARTICIPANT или JUDGE
 * @param {string} attachment.inviteId - ID приглашения (опционально, если сервер возвращает)
 * @param {boolean} isOwn - Является ли текущий пользователь отправителем
 * @param {Function} onAction - Callback при действии (action, inviteId)
 * @param {string} [processedAction] - 'accept' | 'decline' если приглашение уже обработано (у отправителя)
 */
export default function DuelInviteAttachment({ attachment, isOwn, onAction, processedAction }) {
    const [actionLoading, setActionLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);

    const { entityId: duelId, title, inviteType, inviteId } = attachment;

    const formatLabels = {
        PARTICIPANT: 'Участник',
        JUDGE: 'Судья'
    };

    // Если приглашение уже обработано на сервере (у отправителя)
    if (processedAction && !success) {
        const statusLabels = {
            accept: 'Принято',
            decline: 'Отклонено',
            revoke: 'Отозвано'
        };
        return (
            <div className="duel-invite-attachment duel-invite-attachment--success">
                <div className="duel-invite-attachment__header">
                    <span className="duel-invite-attachment__icon">🎤</span>
                    <h4 className="duel-invite-attachment__title">Приглашение в дуэль</h4>
                </div>
                <div className="duel-invite-attachment__body">
                    <div className="duel-invite-attachment__duel">
                        <Link to={`/duels/${duelId}`} className="duel-invite-attachment__duel-link">
                            {title || 'Дуэль'}
                        </Link>
                    </div>
                    <div className="duel-invite-attachment__role">
                        <span className="duel-invite-attachment__role-label">Роль:</span>
                        <span className="duel-invite-attachment__role-value">
                            {formatLabels[inviteType] || inviteType}
                        </span>
                    </div>
                    <div className="duel-invite-attachment__status">
                        <span className="duel-invite-attachment__status-text">
                            {statusLabels[processedAction] || 'Обработано'}
                        </span>
                    </div>
                </div>
            </div>
        );
    }

    // Если сервер не вернул inviteId, ищем его по ID дуэли
    const findInviteByDuelId = async (duelId) => {
        try {
            const invites = await getMyDuelInvites();
            if (!Array.isArray(invites)) return null;

            // Ищем pending приглашение для этой дуэли
            const pendingInvite = invites.find(inv =>
                inv.status === 'PENDING' &&
                inv.duel?.id === duelId
            );

            return pendingInvite?.id || null;
        } catch (err) {
            console.error('[DuelInviteAttachment] Failed to find invite by duelId:', err);
            return null;
        }
    };

    const handleAccept = async () => {
        setActionLoading(true);
        setError(null);

        try {
            let actualInviteId = inviteId;

            // Если сервер не вернул inviteId, ищем по ID дуэли
            if (!actualInviteId && duelId) {
                actualInviteId = await findInviteByDuelId(duelId);
            }

            if (!actualInviteId) {
                setError('Не удалось найти приглашение. Попробуйте принять на странице дуэли.');
                setActionLoading(false);
                return;
            }

            await acceptDuelInvite(actualInviteId);
            setSuccess(true);
            if (onAction) onAction('accept', actualInviteId);
        } catch (err) {
            console.error('Ошибка при принятии приглашения:', err);
            setError(err.response?.data?.message || 'Не удалось принять приглашение');
        } finally {
            setActionLoading(false);
        }
    };

    const handleDecline = async () => {
        setActionLoading(true);
        setError(null);

        try {
            let actualInviteId = inviteId;

            // Если сервер не вернул inviteId, ищем по ID дуэли
            if (!actualInviteId && duelId) {
                actualInviteId = await findInviteByDuelId(duelId);
            }

            if (!actualInviteId) {
                setError('Не удалось найти приглашение. Попробуйте отклонить на странице дуэли.');
                setActionLoading(false);
                return;
            }

            await declineDuelInvite(actualInviteId);
            setSuccess(true);
            if (onAction) onAction('decline', actualInviteId);
        } catch (err) {
            console.error('Ошибка при отклонении приглашения:', err);
            setError(err.response?.data?.message || 'Не удалось отклонить приглашение');
        } finally {
            setActionLoading(false);
        }
    };

    if (success) {
        return (
            <div className="duel-invite-attachment duel-invite-attachment--success">
                <HrnbAlert variant="success" onClose={() => {}}>
                    Приглашение обработано!
                </HrnbAlert>
            </div>
        );
    }

    return (
        <div className="duel-invite-attachment">
            <div className="duel-invite-attachment__header">
                <span className="duel-invite-attachment__icon">🎤</span>
                <h4 className="duel-invite-attachment__title">Приглашение в дуэль</h4>
            </div>

            <div className="duel-invite-attachment__body">
                <div className="duel-invite-attachment__duel">
                    <Link to={`/duels/${duelId}`} className="duel-invite-attachment__duel-link">
                        {title || 'Дуэль'}
                    </Link>
                </div>

                <div className="duel-invite-attachment__role">
                    <span className="duel-invite-attachment__role-label">Роль:</span>
                    <span className="duel-invite-attachment__role-value">
                        {formatLabels[inviteType] || inviteType}
                    </span>
                </div>
            </div>

            {error && (
                <HrnbAlert variant="danger" onClose={() => setError(null)}>
                    {error}
                </HrnbAlert>
            )}

            {!isOwn && !success && (
                <div className="duel-invite-attachment__actions">
                    <HrnbButton
                        variant="success"
                        size="sm"
                        onClick={handleAccept}
                        disabled={actionLoading}
                    >
                        {actionLoading ? 'Принятие...' : 'Принять'}
                    </HrnbButton>
                    <HrnbButton
                        variant="outline-danger"
                        size="sm"
                        onClick={handleDecline}
                        disabled={actionLoading}
                    >
                        {actionLoading ? 'Отклонение...' : 'Отклонить'}
                    </HrnbButton>
                </div>
            )}

            {isOwn && (
                <div className="duel-invite-attachment__status">
                    <span className="duel-invite-attachment__status-text">
                        Ожидает ответа
                    </span>
                </div>
            )}
        </div>
    );
}
